import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Users, CreditCard, BarChart3, Building2, 
  CheckCircle2, ArrowRight, MessageCircle, Mail, Phone,
  Sparkles, Shield, Zap, Globe, ChevronRight, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SEO Meta Component
const SEOHead = ({ content }) => {
  useEffect(() => {
    document.title = content?.meta_title || 'MyRiadManager - Logiciel de Gestion pour Riads et Maisons d\'Hôtes';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', content?.meta_description || 
        'Solution complète de gestion hôtelière pour riads, maisons d\'hôtes et conciergeries. Réservations, facturation, personnel, pilotage financier. PMS cloud made in Morocco.');
    }
  }, [content]);
  return null;
};

export default function LandingPage() {
  const [content, setContent] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, plansRes] = await Promise.all([
          fetch(`${API_URL}/api/landing/content`),
          fetch(`${API_URL}/api/landing/plans`)
        ]);
        
        if (contentRes.ok) {
          const data = await contentRes.json();
          setContent(data);
        }
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlans(plansData.plans || []);
        }
      } catch (e) {
        console.error('Error fetching landing data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const defaultContent = {
    hero_badge: "Logiciel de gestion hôtelière nouvelle génération",
    hero_title: "La plateforme qui transforme la gestion de votre établissement hôtelier",
    hero_subtitle: "Créé pour gérer nos établissements. Conçu pour gérer les vôtres.",
    whatsapp_number: "33670133353",
    contact_email: "contact@myriadmanager.com",
    meta_title: "MyRiadManager - Logiciel de Gestion pour Riads et Maisons d'Hôtes | PMS Hôtel Maroc",
    meta_description: "Solution complète de gestion hôtelière pour riads, maisons d'hôtes et conciergeries. Réservations, facturation, personnel, pilotage financier. PMS cloud made in Morocco."
  };

  const c = { ...defaultContent, ...content };

  const features = [
    {
      icon: Calendar,
      title: "Réservations & Séjours",
      description: "Calendrier centralisé, synchronisation OTA (Booking, Airbnb), gestion des disponibilités et tarifs dynamiques.",
      benefits: ["Sync automatique", "Zéro surbooking", "Vue planning"]
    },
    {
      icon: Users,
      title: "Expérience Client",
      description: "Pré-checkin digital, fiches voyageurs conformes, communication personnalisée et suivi satisfaction.",
      benefits: ["Pré-checkin en ligne", "Fiches RGPD", "Historique client"]
    },
    {
      icon: CreditCard,
      title: "Revenus & Encaissements",
      description: "Suivi des paiements, gestion multi-devises, rapprochement OTA et versements automatiques.",
      benefits: ["Multi-devises", "Versements OTA", "Rapports financiers"]
    },
    {
      icon: BarChart3,
      title: "Charges & Dépenses",
      description: "Suivi des charges récurrentes, dépenses quotidiennes, gestion fournisseurs et échéanciers.",
      benefits: ["Charges auto", "Suivi dépenses", "Alertes échéances"]
    },
    {
      icon: Building2,
      title: "Personnel & Opérations",
      description: "Gestion du personnel, pointage, planning ménage, tâches quotidiennes et suivi heures.",
      benefits: ["Pointage digital", "Planning équipes", "Suivi productivité"]
    },
    {
      icon: Sparkles,
      title: "Pilotage & Décisions",
      description: "Dashboard temps réel, rapports personnalisés, indicateurs clés et export comptable.",
      benefits: ["KPIs en direct", "Exports Excel", "Vision 360°"]
    }
  ];

  const journey = [
    { phase: "Avant l'arrivée", items: ["Réservation confirmée", "Pré-checkin envoyé", "Paiement sécurisé", "Préparation chambre"] },
    { phase: "À l'arrivée", items: ["Check-in rapide", "Fiche voyageur prête", "Clés remises", "Briefing services"] },
    { phase: "Pendant le séjour", items: ["Ventes additionnelles", "Suivi consommations", "Service conciergerie", "Support client"] },
    { phase: "Après le séjour", items: ["Check-out automatique", "Facture générée", "Avis sollicité", "Fidélisation client"] }
  ];

  const targetAudience = [
    { title: "Propriétaires de Riads", desc: "Gérez votre riad comme un pro, même sans équipe dédiée." },
    { title: "Maisons d'Hôtes", desc: "Simplifiez votre quotidien et concentrez-vous sur vos clients." },
    { title: "Conciergeries", desc: "Pilotez plusieurs biens depuis une interface unique." },
    { title: "Groupes & Investisseurs", desc: "Consolidez vos données et optimisez vos performances." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead content={c} />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-28">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_riad-exclusive-ops/artifacts/v8jztajf_Logo%20MYRIADMANAGER.svg" 
                alt="MyRiadManager Logo" 
                className="h-32 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#fonctionnalites" className="text-gray-600 hover:text-gray-900 transition">Fonctionnalités</a>
              <a href="#tarifs" className="text-gray-600 hover:text-gray-900 transition">Tarifs</a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 transition">Contact</a>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="outline" size="sm">Connexion</Button>
              </Link>
              <a href={`https://wa.me/${c.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-6 bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30">
              {c.hero_badge || 'Logiciel de gestion hôtelière nouvelle génération'}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {c.hero_title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 font-light">
              {c.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/login">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 font-semibold px-8">
                  Connexion à l'application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href={`https://wa.me/${c.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-emerald-400 text-emerald-400 hover:bg-emerald-400/10 px-8">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Contacter sur WhatsApp
                </Button>
              </a>
            </div>
            <p className="text-gray-400">
              <Mail className="w-4 h-4 inline mr-2" />
              {c.contact_email}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* Positionnement - Pour qui ? */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Une solution pensée pour votre métier
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fini les fichiers Excel dispersés, les oublis de facturation et les nuits à jongler entre les plateformes. 
              MyRiadManager centralise tout en un seul endroit.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {targetAudience.map((item, i) => (
              <Card key={i} className="border-2 hover:border-emerald-500 transition-colors group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                    <Building2 className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Avant / Après */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-red-800 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center text-red-700">✗</span>
                  Avant MyRiadManager
                </h3>
                <ul className="space-y-3 text-red-700">
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Fichiers Excel pour chaque tâche</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Jongler entre 5 outils différents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Oublis de facturation et erreurs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Aucune vision claire des finances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Stress et perte de temps</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg text-emerald-800 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-emerald-200 rounded-full flex items-center justify-center text-emerald-700">✓</span>
                  Avec MyRiadManager
                </h3>
                <ul className="space-y-3 text-emerald-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Tout centralisé dans une seule interface</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Automatisation des tâches répétitives</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Facturation automatique et fiable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Dashboard financier temps réel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Sérénité et gain de temps</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pourquoi MyRiadManager */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir MyRiadManager ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une plateforme née du terrain, développée par des professionnels de l'hébergement pour des professionnels.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Centralisation complète</h3>
                <p className="text-gray-600">Réservations, finances, personnel, clients : tout dans un seul outil.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Multi-établissements</h3>
                <p className="text-gray-600">Gérez plusieurs riads ou maisons d'hôtes depuis une seule interface.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Pilotage financier</h3>
                <p className="text-gray-600">Vision claire de votre rentabilité, CA, charges et bénéfices en temps réel.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Conformité intégrée</h3>
                <p className="text-gray-600">Fiches voyageurs, RGPD, exports comptables conformes aux normes.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">SaaS Cloud</h3>
                <p className="text-gray-600">Accessible partout, mises à jour automatiques, sauvegardes sécurisées.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">Architecture modulaire</h3>
                <p className="text-gray-600">Activez uniquement les modules dont vous avez besoin.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section id="fonctionnalites" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités complètes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tous les outils dont vous avez besoin pour gérer votre établissement au quotidien.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <Card key={i} className="hover:shadow-lg transition-shadow border-2 hover:border-emerald-200">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {feature.benefits.map((b, j) => (
                      <Badge key={j} variant="secondary" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
                        {b}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Parcours Client */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Un parcours client fluide et maîtrisé
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              De la réservation au check-out, chaque étape est optimisée pour vous et vos clients.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {journey.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 h-full border border-white/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center font-bold">
                      {i + 1}
                    </div>
                    <h3 className="font-semibold text-lg">{step.phase}</h3>
                  </div>
                  <ul className="space-y-2">
                    {step.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-gray-300">
                        <ChevronRight className="w-4 h-4 text-emerald-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {i < journey.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-emerald-500"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section id="tarifs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tarifs transparents
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choisissez la formule adaptée à votre établissement. Sans engagement.
            </p>
          </div>
          
          {plans.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {plans.map((plan, i) => (
                <Card key={plan.id || i} className={`relative overflow-hidden ${plan.is_popular ? 'border-2 border-emerald-500 shadow-xl' : 'border-2'}`}>
                  {plan.is_popular && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                      Populaire
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-gray-900">{plan.price_monthly}</span>
                      <span className="text-gray-600"> MAD/mois</span>
                    </div>
                    <ul className="space-y-3 mb-6">
                      {(plan.features || []).map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <a href={`https://wa.me/${c.whatsapp_number}?text=Bonjour, je suis intéressé par le plan ${plan.name}`} target="_blank" rel="noopener noreferrer">
                      <Button className={`w-full ${plan.is_popular ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} variant={plan.is_popular ? 'default' : 'outline'}>
                        Choisir ce plan
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Contactez-nous pour découvrir nos offres personnalisées.</p>
              <a href={`https://wa.me/${c.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
                <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Demander un devis
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Discutons de votre établissement
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Une question ? Un projet ? Contactez-nous directement, nous répondons rapidement.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href={`https://wa.me/${c.whatsapp_number}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 px-8">
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href={`mailto:${c.contact_email}`}>
              <Button size="lg" variant="outline" className="px-8">
                <Mail className="w-5 h-5 mr-2" />
                {c.contact_email}
              </Button>
            </a>
          </div>
          
          <div className="flex items-center justify-center gap-2 text-gray-500">
            <Phone className="w-4 h-4" />
            <span>+{c.whatsapp_number?.replace(/(\d{2})(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, '+$1 $2 $3 $4 $5 $6')}</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_riad-exclusive-ops/artifacts/v8jztajf_Logo%20MYRIADMANAGER.svg" 
                alt="MyRiadManager Logo" 
                className="h-24 w-auto brightness-0 invert"
              />
            </div>
            
            <div className="flex items-center gap-6 text-gray-400">
              <Link to="/login" className="hover:text-white transition">Connexion</Link>
              <a href={`mailto:${c.contact_email}`} className="hover:text-white transition">{c.contact_email}</a>
              <a href={`https://wa.me/${c.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} MyRiadManager. Tous droits réservés.
          </div>
        </div>
      </footer>
    </div>
  );
}
